import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid/Grid';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import RightArrow from '@serko/zui-icons/es/KeyboardArrowRightIcon';
import Typography from '@serko/zui/es/Typography';
import React, { PureComponent } from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import messages from '../../../../../intl/messages';
import { SegmentTypes } from '../../../../../models/itinerary';
import { AppState } from '../../../../../store/model';
import {
  isSaviSelectIntl,
  shouldShowPricing,
} from '../../../../../store/reducers/itinerary/selectors';
import { findSegment } from '../../../../../store/reducers/segment-details/selectors';
import {
  getRates,
  getRatePrice,
  getRateText,
  getSelectedRate,
  getRateCurrencyCode,
} from '../../../../../utils/rates';
import { RateIcon } from './RateIcon';
import {
  getACarDetailsForSaviSelectIntl,
  getSegmentRatesDetailsForSaviSelectIntl,
} from '../../../../../store/reducers/itinerary/saviSelectIntlSelector';

interface OwnProps {
  type: SegmentTypes;
  segmentId: number;
  optionId: string;
  expanded: boolean;
  nights?: number;
}

interface StateProps {
  selectedRateText: string;
  selectedRatePrice: number;
  selectedRateCode: string;
  ratesCount: number;
  showPrice: boolean;
  days?: number;
  nights?: number;
  isSaviSelectIntl: boolean;
}

interface RatesSummaryProps
  extends OwnProps,
    StateProps,
    WithStyles<typeof styles>,
    InjectedIntlProps {}

class RatesSummaryBase extends PureComponent<RatesSummaryProps> {
  public render() {
    const {
      type,
      expanded,
      classes,
      selectedRateText: description,
      selectedRatePrice: price,
      intl: { formatMessage, formatNumber },
      ratesCount,
      selectedRateCode,
      showPrice,
      isSaviSelectIntl,
    } = this.props;

    return (
      <Grid container zeroMinWidth spacing={16}>
        <Grid item>
          <RateIcon type={type} />
        </Grid>
        <Grid item xs zeroMinWidth>
          <Typography variant="subheading" lineClamp>
            {this.getTitle()}
          </Typography>
          {!expanded && (
            <>
              <Typography variant="body1" lineClamp>
                {description}
              </Typography>
              <Grid
                container
                alignItems="center"
                justify="space-between"
                spacing={16}
              >
                <Grid item>
                  <Button
                    size="small"
                    variant="text"
                    color="primary"
                    className={
                      !isSaviSelectIntl
                        ? classes.button
                        : classes.buttonForSaviSelectIntl
                    }
                  >
                    {formatMessage(messages.options, {
                      numberOfOptions: ratesCount,
                    })}
                    <RightArrow className={classes.iconSmall} />
                  </Button>
                </Grid>
                {showPrice && (
                  <Grid item>
                    <Typography variant="body1" lineClamp>
                      {`${formatNumber(price, {
                        style: 'currency',
                        currency: selectedRateCode,
                        currencyDisplay: 'narrowSymbol',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} ${selectedRateCode}`}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    );
  }
  private getTitle = () => {
    const {
      intl: { formatMessage },
      type,
    } = this.props;

    switch (type) {
      case 'Air':
        return formatMessage(messages.flight.rate.title);
      case 'Hotel':
        return formatMessage(messages.hotel.rate.title);
      case 'Car':
        return formatMessage(messages.car.rate.title);
    }
  };
}

const mapStateToProps = (
  state: AppState,
  { type, segmentId, optionId }: OwnProps,
) => {
  if (!isSaviSelectIntl(state)) {
    const selectedRate = getSelectedRate(
      state,
      type,
      segmentId,
      optionId,
      true,
    )!;
    const { days, nights } = findSegment(segmentId)(state);

    return {
      selectedRateText: getRateText(type, selectedRate),
      selectedRatePrice: getRatePrice(type, selectedRate, days || nights),
      ratesCount: getRates(state, type, segmentId, optionId).length,
      showPrice: shouldShowPricing(state),
      selectedRateCode: getRateCurrencyCode(type, selectedRate),
    };
  } else {
    let selectedRateText = undefined;
    let rateCount = undefined;
    let selectedRatePrice = 0;
    let selectedRateCode = '';
    if (type == 'Hotel') {
      const hotelRates = getSegmentRatesDetailsForSaviSelectIntl(
        segmentId,
        Number(optionId),
      )(state);
      selectedRateText = hotelRates[0]!.roomDescription;
      selectedRatePrice = hotelRates[0]!.fareInfo.totalCost;
      selectedRateCode = hotelRates[0]!.fareInfo.currencyCode;
      rateCount = hotelRates.length;
    } else if (type == 'Car') {
      const car = getACarDetailsForSaviSelectIntl(segmentId, Number(optionId))(
        state,
      );
      selectedRateText = `${car!.carClass} ${car!.carCategory} ${
        car!.carModel.match(/[Aa]ny/) ? '' : car!.carModel
      }`;
      selectedRatePrice = car!.fareInfo.totalCost;
      selectedRateCode = car!.fareInfo.currencyCode;
      rateCount = 1;
    }

    return {
      selectedRateText: selectedRateText,
      selectedRatePrice: selectedRatePrice,
      selectedRateCode: selectedRateCode,
      ratesCount: rateCount,
      showPrice: shouldShowPricing(state),
    };
  }
};

// styled
const styles = (theme: Theme) =>
  createStyles({
    iconSmall: {
      fontSize: 20,
      marginRight: -theme.spacing.unit,
    },
    button: {
      textTransform: 'none',
      marginLeft: -theme.spacing.unit,
    },
    buttonForSaviSelectIntl: {
      textTransform: 'none',
      marginLeft: -theme.spacing.unit,
      color: theme.zeno.main.primary.main,
    },
  });

const enhance = compose<RatesSummaryProps, OwnProps>(
  injectIntl,
  connect(mapStateToProps),
  withStyles(styles),
);

export const RateSummary = enhance(RatesSummaryBase);

RateSummary.displayName = 'RateSummary';
